/**
 * SessionActivityHeatmap
 *
 * A day-of-week × hour-of-day heatmap showing *when* EVs plug in across the
 * week. Cell intensity = number of plug-in events in that hour bucket.
 *
 * Why this is uniquely valuable:
 * - The flex band forecast depends on knowing when cars will be present.
 *   This heatmap makes the underlying behavioral pattern immediately visible.
 * - Operators can spot anomalies ("why are there sessions at 3 AM on Sunday?")
 * - It reveals peak demand windows so grid operators can anticipate headroom
 *   crunch vs. abundance periods before they happen.
 * - Comparing weekday vs. weekend profiles at a glance guides tariff/policy.
 *
 * Metric toggle: plug-in count (how many sessions started) vs. average
 * session duration in that slot (how long they stay), giving two complementary
 * views of behavioral patterns.
 *
 * Built with pure SVG cells — no extra charting library.
 */

import { useCallback, useMemo, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { InfoTip } from '@/components/ui/info-tip'
import { Loader2, CalendarDays } from 'lucide-react'
import { useChargingSessions } from '@/hooks/use-monitoring'
import type { TimeRange } from '@/hooks/use-monitoring'
import type { components } from '../../../../../types/flex-api'

type ChargingSession = components['schemas']['ChargingSession']

interface SessionActivityHeatmapProps {
  poolID: string
  timeRange: TimeRange
}

type Metric = 'count' | 'duration'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = Array.from({ length: 24 }, (_, i) => i)

// Return 0=Mon … 6=Sun  (JS getDay: 0=Sun, so we rotate)
function dayIndex(date: Date): number {
  return (date.getDay() + 6) % 7
}

function hourLabel(h: number): string {
  if (h === 0) return '12a'
  if (h < 12) return `${h}a`
  if (h === 12) return '12p'
  return `${h - 12}p`
}

// Map a 0-1 intensity to a Tailwind-compatible HSL color using a blue-to-indigo ramp
function intensityColor(t: number, isDark = false): string {
  // t in [0, 1]
  if (t <= 0) return isDark ? 'hsl(220,20%,14%)' : 'hsl(220,20%,96%)'
  // Interpolate from light blue → deep indigo
  const lightness = isDark ? 14 + (1 - t) * 28 : 96 - t * 64
  const saturation = 60 + t * 35
  const hue = 230 - t * 20
  return `hsl(${hue},${saturation}%,${lightness}%)`
}

const LABEL_LEFT = 36
const LABEL_TOP = 20

export default function SessionActivityHeatmap({ poolID, timeRange }: SessionActivityHeatmapProps) {
  const [metric, setMetric] = useState<Metric>('count')
  const [hoveredCell, setHoveredCell] = useState<{ day: number; hour: number } | null>(null)

  // Responsive dimensions — measured from the SVG container via ResizeObserver
  const [svgWidth, setSvgWidth] = useState(600)
  const [svgHeight, setSvgHeight] = useState(200)
  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      if (width > 0) setSvgWidth(width)
      if (height > 0) setSvgHeight(height)
    })
    ro.observe(node)
    const rect = node.getBoundingClientRect()
    if (rect.width > 0) setSvgWidth(rect.width)
    if (rect.height > 0) setSvgHeight(rect.height)
    return () => ro.disconnect()
  }, [])

  // Cell dimensions derived from available space
  const cellW = (svgWidth - LABEL_LEFT - 4) / 24
  const cellH = (svgHeight - LABEL_TOP - 4) / 7

  const { data: sessions = [], isLoading } = useChargingSessions(poolID, timeRange)

  // Build 7×24 grid: count and total duration per bucket
  const grid = useMemo(() => {
    const countGrid: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0))
    const durationGrid: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0))

    sessions.forEach((s: ChargingSession) => {
      const plugDate = new Date(s.pluggedAt)
      const day = dayIndex(plugDate)
      const hour = plugDate.getHours()
      countGrid[day][hour]++
      const unplugDate = new Date(s.unpluggedAt)
      const durationMins = (unplugDate.getTime() - plugDate.getTime()) / 60000
      durationGrid[day][hour] += durationMins
    })

    // Compute average duration
    const avgDurationGrid = durationGrid.map((row, d) =>
      row.map((total, h) => (countGrid[d][h] > 0 ? total / countGrid[d][h] : 0))
    )

    return { countGrid, avgDurationGrid }
  }, [sessions])

  const activeGrid = metric === 'count' ? grid.countGrid : grid.avgDurationGrid

  // Find max for normalization
  const maxVal = useMemo(() => Math.max(1, ...activeGrid.flatMap((row) => row)), [activeGrid])

  const formatCellValue = (v: number): string => {
    if (metric === 'count') return `${v} session${v !== 1 ? 's' : ''}`
    if (v === 0) return 'no sessions'
    const h = Math.floor(v / 60)
    const m = Math.round(v % 60)
    return h > 0 ? `avg ${h}h ${m}m` : `avg ${m}m`
  }

  const totalSvgW = svgWidth
  const totalSvgH = svgHeight

  // Marginal summaries — switch with metric
  // count mode:    sum of sessions per day / per hour
  // duration mode: average duration (mins) per day / per hour (ignoring zero cells)
  const dayMarginals = useMemo(() => {
    if (metric === 'count') {
      return grid.countGrid.map((row) => row.reduce((a, b) => a + b, 0))
    }
    return grid.avgDurationGrid.map((row, d) => {
      const nonZero = row.filter((_, h) => grid.countGrid[d][h] > 0)
      return nonZero.length > 0 ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length : 0
    })
  }, [metric, grid])

  const hourMarginals = useMemo(() => {
    if (metric === 'count') {
      return HOURS.map((h) => grid.countGrid.reduce((sum, row) => sum + row[h], 0))
    }
    return HOURS.map((h) => {
      const vals = grid.avgDurationGrid.map((row, d) => ({ v: row[h], n: grid.countGrid[d][h] }))
      const nonZero = vals.filter(({ n }) => n > 0)
      return nonZero.length > 0 ? nonZero.reduce((s, { v }) => s + v, 0) / nonZero.length : 0
    })
  }, [metric, grid])

  const maxDayMarginal = Math.max(1, ...dayMarginals)
  const maxHourMarginal = Math.max(1, ...hourMarginals)

  const formatMarginalDuration = (mins: number): string => {
    if (mins === 0) return '0m'
    const h = Math.floor(mins / 60)
    const m = Math.round(mins % 60)
    return h > 0 ? `${h}h${m > 0 ? `${m}m` : ''}` : `${m}m`
  }

  const hoveredValue = hoveredCell !== null ? activeGrid[hoveredCell.day][hoveredCell.hour] : null

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Session Activity Heatmap
              <InfoTip
                content={
                  <>
                    <p className="font-medium mb-1">Session Activity Heatmap</p>
                    <p>
                      Each cell represents one hour of one day of the week. Darker colour = more
                      activity in that slot. Use this to spot recurring plug-in patterns — e.g.
                      Monday morning rush or weekend overnight sessions.
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      Hover any cell to see the exact value. Toggle between <strong>Count</strong>{' '}
                      (how many sessions started) and <strong>Duration</strong> (how long cars
                      stayed on average).
                    </p>
                  </>
                }
              />
            </CardTitle>
            <CardDescription>
              When do EVs plug in? Day-of-week × hour-of-day activity patterns
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Metric toggle */}
            <div className="flex items-center gap-1.5">
              <InfoTip
                content={
                  <>
                    <p className="font-medium mb-1">Count vs Duration</p>
                    <p>
                      <strong>Count</strong> — how many EV sessions started in each day/hour slot.
                      Bright cells = high plug-in frequency.
                    </p>
                    <p className="mt-1">
                      <strong>Duration</strong> — average time cars remained plugged in for sessions
                      that started in that slot. Useful for spotting overnight parkers vs. quick
                      top-ups.
                    </p>
                  </>
                }
              />
              <div className="flex gap-1 rounded-md border p-0.5">
                <button
                  onClick={() => setMetric('count')}
                  className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                    metric === 'count'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Count
                </button>
                <button
                  onClick={() => setMetric('duration')}
                  className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                    metric === 'duration'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Duration
                </button>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 min-h-0 overflow-hidden p-6 pt-0 flex flex-col gap-4">
        {isLoading && sessions.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No charging sessions found</p>
            <p className="text-sm mt-2">No EV charging activity in the selected time range</p>
          </div>
        ) : (
          <>
            {/* Hovered cell detail */}
            <div className="h-6 shrink-0 text-sm text-muted-foreground">
              {hoveredCell !== null && hoveredValue !== null ? (
                <span>
                  <strong className="text-foreground">
                    {DAYS[hoveredCell.day]}, {hoveredCell.hour}:00–{hoveredCell.hour + 1}:00
                  </strong>
                  {' — '}
                  {formatCellValue(hoveredValue)}
                </span>
              ) : (
                <span className="italic opacity-60">Hover a cell to see details</span>
              )}
            </div>

            {/* Main heatmap — fills all remaining space */}
            <div ref={measuredRef} className="w-full flex-1 min-h-0">
              <svg
                viewBox={`0 0 ${totalSvgW} ${totalSvgH}`}
                width={svgWidth}
                height={svgHeight}
                style={{ display: 'block', width: '100%', height: '100%' }}
              >
                {/* Hour labels (top) */}
                {HOURS.map((h) => (
                  <text
                    key={h}
                    x={LABEL_LEFT + h * cellW + cellW / 2}
                    y={14}
                    textAnchor="middle"
                    fontSize={9}
                    fill="currentColor"
                    opacity={0.5}
                  >
                    {h % 3 === 0 ? hourLabel(h) : ''}
                  </text>
                ))}

                {/* Day labels (left) + cells */}
                {DAYS.map((day, d) => (
                  <g key={day}>
                    <text
                      x={LABEL_LEFT - 4}
                      y={LABEL_TOP + d * cellH + cellH / 2 + 4}
                      textAnchor="end"
                      fontSize={11}
                      fill="currentColor"
                      opacity={0.55}
                    >
                      {day}
                    </text>
                    {HOURS.map((h) => {
                      const val = activeGrid[d][h]
                      const t = val / maxVal
                      const isHovered = hoveredCell?.day === d && hoveredCell?.hour === h
                      return (
                        <rect
                          key={h}
                          x={LABEL_LEFT + h * cellW + 1}
                          y={LABEL_TOP + d * cellH + 1}
                          width={cellW - 2}
                          height={cellH - 2}
                          rx={3}
                          fill={intensityColor(t)}
                          stroke={isHovered ? 'hsl(var(--foreground))' : 'transparent'}
                          strokeWidth={isHovered ? 1.5 : 0}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={() => setHoveredCell({ day: d, hour: h })}
                          onMouseLeave={() => setHoveredCell(null)}
                        />
                      )
                    })}
                  </g>
                ))}
              </svg>
            </div>

            {/* Marginal bar charts */}
            <div className="shrink-0 grid grid-cols-2 gap-6 pt-2 border-t">
              {/* By day of week */}
              <div>
                <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  {metric === 'count' ? 'Sessions by day' : 'Avg duration by day'}
                  <InfoTip
                    content={
                      metric === 'count' ? (
                        <>
                          <p className="font-medium mb-1">Sessions by day</p>
                          <p>
                            Total number of charging sessions that started on each day of the week,
                            summed across the selected time window. Longer bar = more plug-in events
                            on that day.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium mb-1">Avg duration by day</p>
                          <p>
                            Average session length (in hours/minutes) for sessions that started on
                            each day of the week. Computed only over hours that had at least one
                            session — empty slots don't pull the average down.
                          </p>
                        </>
                      )
                    }
                  />
                </p>
                <div className="space-y-1">
                  {DAYS.map((day, d) => (
                    <div key={day} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-7 text-right">{day}</span>
                      <div className="flex-1 h-3 bg-muted rounded-sm overflow-hidden">
                        <div
                          className="h-3 bg-indigo-500/70 rounded-sm transition-all duration-300"
                          style={{ width: `${(dayMarginals[d] / maxDayMarginal) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground w-10 tabular-nums text-right">
                        {metric === 'count'
                          ? dayMarginals[d]
                          : formatMarginalDuration(dayMarginals[d])}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By hour of day */}
              <div>
                <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  {metric === 'count' ? 'Sessions by hour' : 'Avg duration by hour'}
                  <InfoTip
                    content={
                      metric === 'count' ? (
                        <>
                          <p className="font-medium mb-1">Sessions by hour</p>
                          <p>
                            Total plug-in events per hour of day, aggregated across all days in the
                            selected window. Taller bar = that hour sees more session starts. Hover
                            a bar to see the exact count.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium mb-1">Avg duration by hour</p>
                          <p>
                            Average session length for cars that plugged in during each hour of the
                            day. A tall bar at midnight means sessions starting then tend to run
                            longer (likely overnight parkers).
                          </p>
                        </>
                      )
                    }
                  />
                </p>
                <div className="flex items-end gap-0.5 h-16">
                  {HOURS.map((h) => (
                    <div
                      key={h}
                      title={
                        metric === 'count'
                          ? `${h}:00 — ${hourMarginals[h]} session${hourMarginals[h] !== 1 ? 's' : ''}`
                          : `${h}:00 — avg ${formatMarginalDuration(hourMarginals[h])}`
                      }
                      className="flex-1 rounded-t-sm transition-all duration-300"
                      style={{
                        height: `${(hourMarginals[h] / maxHourMarginal) * 100}%`,
                        backgroundColor: 'hsl(230, 70%, 60%)',
                        opacity: 0.7,
                        minHeight: hourMarginals[h] > 0 ? '2px' : '0',
                      }}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>12a</span>
                  <span>6a</span>
                  <span>12p</span>
                  <span>6p</span>
                  <span>12a</span>
                </div>
              </div>
            </div>

            {/* Color scale legend */}
            <div className="shrink-0 flex items-center gap-3 text-xs text-muted-foreground">
              <span>Less</span>
              <div className="flex h-3 flex-1 overflow-hidden rounded-sm max-w-[120px]">
                {Array.from({ length: 12 }, (_, i) => (
                  <div
                    key={i}
                    className="flex-1 h-3"
                    style={{ backgroundColor: intensityColor(i / 11) }}
                  />
                ))}
              </div>
              <span>More</span>
              <span className="ml-2 opacity-60">
                ({metric === 'count' ? 'plug-in count' : 'avg session length'})
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
